import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  getDataSources,
  magnitudePointFormatter,
} from '../utils/compass'
import { DAY_HOURS_30_MINUTES_STEPS } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

interface CompassAPITimeValueDataPoint {
  time: string
  value: number
}

interface CompassAPIIntradayChartData {
  title: string
  data_sources: {
    text: string
    href: string
    last_capture: any
  }[]
  data: {
    name: string
    data: CompassAPITimeValueDataPoint[]
  }[]
}

const defaultColumnChartConfig = (pointWidth?: number) => ({
  plotOptions: { column: { pointWidth: pointWidth ?? 60, stacking: 'normal' } },
  tooltip: { pointFormatter: magnitudePointFormatter(), xDateFormat: '%B %Y' },
  xAxis: { labels: { format: '{value:%Y}' } },
})

export namespace Payments {
  export namespace Pix {
    const CONTEXT_ID = 'equity:payments:pix'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'daily_liquidated'
        | 'intraday'
        | 'key_nature'
        | 'key_type'
        | 'registered_accounts'
        | 'transaction_statistics_value'
        | 'transaction_statistics_volume'
        | 'transaction_nature_value'
        | 'transaction_nature_volume'
        | 'transaction_age'
        | 'transaction_region'
        | 'registered_users'
        | 'users_completed_transactions'
      context: typeof CONTEXT_ID
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CompassAPIIntradayChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'daily_liquidated':
            return getDailyLiquidatedChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'intraday':
            return getIntradayChartProps(
              config,
              data as CompassAPIIntradayChartData
            )
          case 'key_nature':
            return getKeyNatureChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'key_type':
            return getKeyTypeChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'registered_accounts':
            return getRegisteredAccountsChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'transaction_statistics_value':
          case 'transaction_statistics_volume':
          case 'registered_users':
            return getTransactionStatisticsChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'transaction_nature_value':
          case 'transaction_nature_volume':
            return getTransactionNatureChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'transaction_age':
          case 'transaction_region':
            return getTransactionAgeRegionChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'users_completed_transactions':
            return getUserCompletedTransactionsChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
        }
      },
    }

    const getData = (
      data: CompassAPIMultiSeriesChartData,
      filter: 'Valor' | 'Quantidade'
    ) => ({
      ...data,
      data: data.data.filter((s) => s.name.includes(filter)),
    })

    const getDailyLiquidatedChartProps = getMultipleLineChartProps(
      Context,
      {
        tooltip: { pointFormatter: magnitudePointFormatter() },
        xAxis: { labels: { format: '{value:%Y}' } },
        yAxis: [{ id: 'primary' }, { id: 'secondary', opposite: true }],
      },
      {
        yAxis: ['primary', 'primary', 'secondary'],
      }
    )

    const getIntradayChartProps = (
      config: ChartConfig,
      data: CompassAPIIntradayChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        id: `series-${idx}`,
        type: 'spline',
        marker: {
          enabled: false,
        },
        name: s.name,
        lineWidth: 2,
        yAxis: idx === 1 ? 'primary' : 'secondary',
        data: s.data.map(({ time, value }) => [time, value]),
      }))

      const yAxis = [
        {
          id: 'primary',
          opposite: false,
          title: { text: ' ' },
        },
        {
          id: 'secondary',
          opposite: true,
          title: { text: ' ' },
        },
      ]
      const xAxis = [
        {
          type: 'category',
          categories: DAY_HOURS_30_MINUTES_STEPS,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          series,
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
          xAxis,
          yAxis,
        },
        useRegularChart: true,
        stockTools: true,
      }
    }

    const getKeyNatureChartProps = getMultipleColumnChartProps(
      Context,
      defaultColumnChartConfig(40),
      {
        color: ['#297F0D', '#808080', '#16164D'],
        type: ['column', 'column', 'spline'],
      }
    )

    const getKeyTypeChartProps = getMultipleColumnChartProps(
      Context,
      defaultColumnChartConfig(),
      {
        color: [
          '#297F0D',
          '#808080',
          '#e7b619',
          '#f57350',
          '#38387C',
          '#16164D',
        ],
        type: ['column', 'column', 'column', 'column', 'column', 'spline'],
      }
    )

    const getRegisteredAccountsChartProps = getMultipleColumnChartProps(
      Context,
      defaultColumnChartConfig(),
      {
        type: ['spline', 'column', 'column', 'column'],
      }
    )

    const getTransactionStatisticsChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, defaultColumnChartConfig(), {
        color: ['#16164D', '#297F0D', '#808080'],
        type: ['spline', 'column', 'column'],
      })(
        config,
        config.chart === 'transaction_statistics_value'
          ? getData(data, 'Valor')
          : config.chart === 'transaction_statistics_volume'
          ? getData(data, 'Quantidade')
          : data
      )

    const getTransactionNatureChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          legend: { maxHeight: 80 },
          plotOptions: { column: { pointWidth: 60, stacking: 'normal' } },
          tooltip: {
            shared: true,
            split: false,
            xDateFormat: '%B %Y',
            pointFormatter: magnitudePointFormatter(),
          },
        },
        {
          color: [
            '#16164D',
            '#297F0D',
            '#808080',
            '#e7b619',
            '#f57350',
            '#38387C',
            '#56CC2B',
          ],
          type: Array(data.data.length).fill('column'),
        }
      )(
        config,
        config.chart === 'transaction_nature_value'
          ? getData(data, 'Valor')
          : getData(data, 'Quantidade')
      )

    const getTransactionAgeRegionChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        convertToPercentages: Array(data.data.length).fill(true),
        plotOptions: { column: { pointWidth: 60, stacking: 'normal' } },
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    const getUserCompletedTransactionsChartProps = getMultipleColumnChartProps(
      Context,
      {
        plotOptions: { column: { pointPadding: 0.05, minPointLength: 2 } },
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          xDateFormat: '%B %Y',
        },
        xAxis: { labels: { format: '{value:%Y}' } },
      },
      {
        color: ['#16164D', '#a1a1b7', '#297F0D', '#a9cb9e'],
        showInLegend: [true, true, true, true, false, false],
        type: ['column', 'column', 'column', 'column', 'column', 'spline'],
        visible: [true, true, true, true, false, false],
      }
    )
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:payments:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_pags_us'
        | 'pe_stne_us'
        | 'pe_sq_us'
        | 'pe_adyen_na'
        | 'pe_meli_us'
        | 'pe_gett11_bz'
        | 'pe_ciel3_bz'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            msdIndicators: { avg: true, sd1: true, sd2: true },
          },
          { name: [''] }
        )(config, data),
    }
  }
}
